import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { useFieldArray, Controller, Control } from "react-hook-form";
import { Users, Plus, Trash2 } from "lucide-react";
import { FormLabel } from "@ui/internal/components/label";
import { IconPicker } from "@components/ui/icon-picker";

interface AudienceContainerProps {
  control: Control<any>;
}

const AudienceContainer = ({ control }: AudienceContainerProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "audience.values",
  });

  return (
    <div className="w-full space-y-8 mt-10">
      <div className="space-y-4">
        <Controller
          control={control}
          name="audience.eyebrow"
          render={({ field, fieldState: { error } }) => (
            <div className="space-y-1">
              <FormLabel label="Eyebrow" />
              <Input
                {...field}
                placeholder="e.g. Who is this for?"
                className="max-w-md"
              />
              {error && (
                <span className="text-xs text-red-500">{error.message}</span>
              )}
            </div>
          )}
        />

        <Controller
          control={control}
          name="audience.heading"
          render={({ field, fieldState: { error } }) => (
            <div className="space-y-1">
              <FormLabel label="Heading" />
              <Input
                {...field}
                placeholder="e.g. Designed for aspiring creatives"
                className="max-w-xl"
              />
              {error && (
                <span className="text-xs text-red-500">{error.message}</span>
              )}
            </div>
          )}
        />
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          Audience Values
        </h3>

        <div className="space-y-4">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="p-5 rounded-md border bg-white shadow-sm relative group animate-in fade-in slide-in-from-top-2"
            >
              <button
                type="button"
                onClick={() => remove(index)}
                className="absolute top-5 right-5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 size={18} />
              </button>

              <div className="flex items-center gap-2 text-emerald-600 mb-2">
                <Users size={20} />
                <h4 className="font-semibold text-md">
                  Value {index + 1}
                </h4>
              </div>

              <div className="space-y-4">
                <Controller
                  control={control}
                  name={`audience.values.${index}.icon`}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <FormLabel label="Icon" />
                      <IconPicker
                        value={field.value}
                        onValueChange={(icon) => field.onChange(icon)}
                      />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={`audience.values.${index}.title`}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <FormLabel label="Title" />
                      <Input
                        {...field}
                        placeholder="e.g. Beginner-friendly"
                      />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={`audience.values.${index}.description`}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <FormLabel label="Description" />
                      <Textarea
                        {...field}
                        placeholder="Enter details..."
                        className="min-h-[80px] resize-none"
                      />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() =>
            append({ title: "", description: "", icon: "" })
          }
          className="mt-4 w-full py-8 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center text-gray-500 hover:border-emerald-500 hover:text-emerald-600 transition-colors bg-gray-50/50 hover:bg-emerald-50/50"
        >
          <Plus size={24} className="mb-2" />
          <span className="font-medium">Add Audience Value</span>
        </button>
      </div>
    </div>
  );
};

export default AudienceContainer;